<template>
  <div class="flex h-13">
    <div class="analytics-first-col-sticky pl-6 flex bg-slate-100">
      <div
        class="flex items-center bg-pure-white border-b border-l border-r border-slate-400"
        :class="{ 'rounded-bl': isLast }"
      >
        <div class="w-20 px-4 flex flex-shrink-0">
          <BlTextBody2 :text="item.id" class="text-slate-1000" />
        </div>
        <div class="w-60 px-4 flex flex-shrink-0" :title="item.client.name">
          <BlTextBody2
            :text="item.client.name"
            class="text-slate-1000 truncate"
          />
        </div>
      </div>
    </div>
    <div
      v-if="enableBranch"
      class="w-50 px-4 flex items-center gap-2 flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 v-if="!item.room.branch" text="-" />
      <div
        v-else
        class="h-7 min-w-12 px-2 flex items-center justify-center rounded truncate"
        :style="{
          backgroundColor: item.room.branch.background_color || 'unset'
        }"
        :title="item.room.branch.name"
      >
        <BlTextBody2
          :text="item.room.branch.name || '-'"
          class="truncate"
          :style="{ color: item.room.branch.color || 'unset' }"
        />
      </div>
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2
        v-if="!item.user"
        title="Deleted user"
        text="Deleted user"
        class="italic truncate"
      />
      <BlTextBody2
        v-else
        :title="item.user.name"
        :text="item.user.name"
        class="truncate"
      />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 :text="displayDate(item.date)" />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2
        :text="`${item.start_time_string}-${item.end_time_string}`"
      />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 :text="item.duration_hours || '-'" />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 :text="cancelledByOptions[item.cancelled_by] || '-'" />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 :text="cancellationReasonText" class="capitalize-first" />
    </div>
    <div
      class="w-50 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2 :text="subtractSessionCreditText" />
    </div>
    <div
      class="w-50 px-4 flex items-center gap-2 flex-shrink-0 bg-pure-white border-b border-slate-400"
    >
      <BlTextBody2
        v-if="!item.client.labels || !item.client.labels.length"
        text="-"
      />
      <div
        v-else
        v-for="i in item.client.labels.slice(0, 2)"
        :key="i.id"
        class="h-7 min-w-12 px-2 flex items-center justify-center rounded truncate bg-slate-300"
        :title="i.name"
      >
        <BlTextBody2 :text="i.name || '-'" class="truncate" />
      </div>
      <div class="relative" v-if="item.client.labels.slice(2).length">
        <div
          class="flex items-center justify-center cursor-pointer"
          @click="moreTags = !moreTags"
        >
          <BlBubbleCount
            :count="`+ ${item.client.labels.slice(2).length}`"
            class="h-7 hover:bg-light-purple-100"
          />
        </div>
        <div
          v-if="moreTags"
          class="absolute top-0 right-0 min-w-32 mt-10 p-1 flex flex-col gap-1 bg-pure-white border border-grey-200 rounded truncate"
          :style="{ zIndex: 1 }"
        >
          <div
            v-for="(i, idx) in item.client.labels.slice(2)"
            :key="idx"
            class="flex max-w-64"
          >
            <div
              :title="i.name || '-'"
              class="px-4 py-2 text-left rounded truncate"
            >
              <BlTextBody2 class="truncate" :text="i.name || '-'" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="analytics-last-col-sticky pr-6 flex bg-slate-100 flex-grow">
      <div
        class="w-32 px-2 flex items-center justify-end flex-shrink-0 flex-grow bg-pure-white border-b border-r border-slate-400"
        :class="{ 'rounded-br': isLast }"
      >
        <router-link
          :to="`/appointments/details/${item.id}`"
          target="_blank"
          title="View details"
        >
          <BlInputButton type="outlined" dense label="View details" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "RowCancellationOverview",
  components: {
    BlTextSubtitle3,
    BlTextBody2,
    BlChip,
    BlBubbleCount,
    BlInputButton
  },
  props: {
    item: { type: Object, required: true },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      moreTags: false,
      cancelledByOptions: {
        client: "Client",
        therapist: "Therapist",
        clinic_or_admin: "Admin/clinic"
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "centerReasonSettings"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    cancellationReasonText() {
      if (!this.item) return "-";
      if (!this.item.cancellation_reason) return "-";

      const cancelReason = this.item.cancellation_reason;
      const cancelCenterReason = this.centerReasonSettings.find(
        i => i.formatted_name === cancelReason
      );

      if (cancelCenterReason) {
        if (cancelCenterReason.is_default) {
          return this.item.other_cancellation_reason || cancelCenterReason.name;
        } else return cancelCenterReason.name;
      }
      if (cancelReason) return cancelReason.replaceAll("_", " ");
    },
    subtractSessionCreditText() {
      if (!this.item) return "-";
      const subtract = this.item.subtract_session_credit;
      if (subtract === null) return "-";
      return subtract ? "Yes" : "No";
    }
  },
  watch: {
    moreTags(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerTags);
        }, 10);
      }
    }
  },
  methods: {
    displayDate(date, format = "DD/MM/YYYY") {
      if (!date) return "-";
      return moment(date).format(format);
    },
    closeHandlerTags() {
      window.removeEventListener("click", this.closeHandlerTags);
      this.moreTags = false;
    }
  }
};
</script>

<style scoped lang="scss">
.analytics-first-col-sticky {
  position: sticky;
  left: 15rem;
  z-index: 1;
}
.analytics-last-col-sticky {
  position: sticky;
  right: 0rem;
  z-index: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/analytics/table/RowCancellationOverview.vue